<script lang="ts">
	import { Helper, Input } from "flowbite-svelte-v1";
	import { createEventDispatcher, getContext } from "svelte";
	import type { Field } from "#gen/forms/v1/field_pb.js";
	import { getValidatorWithErrors } from "#lib/ajv.js";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import DefaultValue from "#lib/components/page/components/form/DefaultValue.svelte";
	import FieldHeader from "#lib/components/page/components/form/FieldHeader.svelte";
	import type { FieldProps, TypeIcon } from "#lib/components/page/components/form/FieldProps.js";
	import { getDefaultTextAsStruct, getDefaultTextValue } from "#lib/components/page/components/form/defaultValue.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import { ERR_REQUIRED } from "#lib/validation.js";
	import type { JsonObject } from "@bufbuild/protobuf";
	import type { Writable } from "svelte/store";

	type $$Props = FieldProps;
	interface $$Events {
		input: CustomEvent<Field>;
		change: CustomEvent<Field>;
	}

	const dispatch = createEventDispatcher();
	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");

	export let typeIcon: TypeIcon | undefined = undefined;
	export let field: Field;
	export let editable = false;
	export let active = false;
	export let value: string | (string[] & string) | undefined = !editable ? getDefaultTextValue(field.defaultValue) : undefined;
	export let readOnly = false;

	const inputDisabled = (editable && field.immutable) || readOnly || !field.active;
	let error: string | undefined = undefined;

	$: !editable && (value = getDefaultTextValue(field.defaultValue));

	export async function validate(): Promise<boolean> {
		if (field.required && !value) {
			error = ERR_REQUIRED;
			return false;
		} else if (field.validationSchema?.length > 0 && value) {
			const validator = await getValidatorWithErrors(field.validationSchema);
			const isValid = validator(value);
			if (!isValid) {
				error = validator.errors?.[0]?.message ?? "";
				return false;
			}
		}
		error = "";
		return true;
	}

	function handleValueChange(event: Event) {
		const target = event.target as HTMLInputElement;
		value = target.value;
	}

	function handleDefaultValue(event: CustomEvent<string | undefined>) {
		field.defaultValue = getDefaultTextAsStruct(event.detail) ?? ({} as JsonObject);
		dispatch("input", field);
	}

	function handleFieldChange() {
		dispatch("change", field);
	}
</script>

<div {...$$restProps}>
	{#if editable}
		<FieldHeader {typeIcon} {active} {field} on:input on:change />
	{:else}
		<label for={field.name} class="mb-2 inline-block text-sm font-medium" class:required={field.required}>
			{field.title}
		</label>
		{#if field.description}
			<div class="field-description my-2 text-xs text-gray-500">{field.description}</div>
		{/if}
	{/if}
	{#if field.static && !editable}
		<DefaultValue>{getDefaultTextValue(field.defaultValue)}</DefaultValue>
	{:else if editable}
		<PlaintextEditor provider={$tokenProvider} value={getDefaultTextValue(field.defaultValue)} disabled={inputDisabled} on:input={handleDefaultValue} on:change={handleFieldChange} />
	{:else}
		<Input
			onblur={() => {
				if (!editable) {
					validate();
				}
			}}
			class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
			color={error ? "red" : "default"}
			{value}
			type="text"
			name={field.name}
			id={field.name}
			disabled={inputDisabled}
			autocomplete="name"
			oninput={handleValueChange}
		/>
		{#if error}
			<Helper class="mt-2 text-red-700"><span class="font-medium">{error}</span></Helper>
		{/if}
	{/if}
</div>
